import _ from 'lodash'
import { Concept, ConceptsByCid } from "../concept/Concept"
import { ConceptValueDataType } from "@fusion-advantage/backcompat"
import { ProposedPrompt, ProposedIntersection, ProposedResponse } from "./ProposedEntity"
import { PromptDataType } from "./Prompt"
import { ReturnedEntityState } from "./ReturnedEntity"
import { ReturnedEntities, ReturnedResponse } from "./ReturnedEntity"

export class ConceptProposedEntities {
	prompts: ProposedPrompt[] = []
	responses: ProposedResponse[] = []
	responsesBySubconceptCid: {[cid: string]: ProposedResponse} = {}
	intersections: ProposedIntersection[] = []
}

export interface PersistModel {
	get name(): string
	getProposedEntities(concept: Concept, subconcepts: ConceptsByCid): ConceptProposedEntities
	getReturnedEntities(proposedEntities: ConceptProposedEntities, returnedEntities: ReturnedEntityState): ReturnedEntities
}

const ERR_INVALID_VALUEDATATYPE = (concept: Concept, model: PersistModel) =>
	`cannot model ${concept.valueDataType} with ${model.name}`
const SUGGEST_INTERSECTIONS = '(Do you need to run branchProposeIntersections?)'

// 2023-11-26
// except for boolean groups which are modeled 1ppg1rpo
// (currently, text fields and individual checks)
// values for an Encounter are stored in a Result
// where the Response is this Generic Response
const GenericProposedResponse = new ProposedResponse(null, 'Changeling')

// One prompt, Generic response
// e.g. single checkbox, text field
export const model1pGr = new (class implements PersistModel {
	get name() {
		return '1pGr'
	}
	getProposedEntities(concept: Concept, _subconcepts: ConceptsByCid): ConceptProposedEntities {
		const entities = new ConceptProposedEntities()
		if(concept.title === null) {
			// concepts without titles cannot be persisted;
			// there is no natural key to match with the database
			return entities
		}
		let promptDataType: PromptDataType
		switch(concept.valueDataType) {
			case ConceptValueDataType.Check:
				promptDataType = PromptDataType.Boolean
				break
			case ConceptValueDataType.File:
			case ConceptValueDataType.String:
				promptDataType = PromptDataType.String
				break
			case ConceptValueDataType.Date:
				promptDataType = PromptDataType.Date
				break
			case ConceptValueDataType.JSON:
			case ConceptValueDataType.Object:
				promptDataType = PromptDataType.JSON
				break
			case ConceptValueDataType.Decimal:
			case ConceptValueDataType.Integer:
				promptDataType = PromptDataType.Number
				break
			default:
				throw new Error(ERR_INVALID_VALUEDATATYPE(concept, this))
		}
		const prompt = new ProposedPrompt(concept.cid, concept.title, promptDataType)
		entities.prompts.push(prompt)
		const response = GenericProposedResponse
		entities.responses.push(response)
		const intersection = new ProposedIntersection(prompt.stateKey, response.stateKey)
		entities.intersections.push(intersection)
		return entities
	}
	_getPromptResponse(proposedEntities: ConceptProposedEntities, returnedEntities: ReturnedEntityState) {
		if(proposedEntities.prompts.length !== 1) {
			throw new Error(`[model ${this.name} getPromptResponse] expected 1 proposed prompt`)
		}
		if(proposedEntities.responses.length !== 1) {
			throw new Error(`[model ${this.name} getPromptResponse] expected 1 proposed response`)
		}
		const promptKey = proposedEntities.prompts[0].stateKey
		const responseKey = proposedEntities.responses[0].stateKey
		const prompt = returnedEntities.prompt[promptKey]
		const response = returnedEntities.response[responseKey]
		if(!prompt) {
			throw new Error(`[model ${this.name} getPromptResponse] expected prompt ${promptKey} not found ${SUGGEST_INTERSECTIONS}`)
		}
		if(!response) {
			throw new Error(`[model ${this.name} getPromptResponse] expected response ${responseKey} not found ${SUGGEST_INTERSECTIONS}`)
		}
		return { prompt, response }
	}
	getReturnedEntities(proposedEntities: ConceptProposedEntities, returnedEntities: ReturnedEntityState): ReturnedEntities {
		const { prompt, response } = this._getPromptResponse(proposedEntities, returnedEntities)
		return {
			prompts: [prompt],
			responses: [response],
		}
	}
})

// One prompt per group, One response per option
// e.g. radio group, check group
export const model1ppg1rpo = new (class implements PersistModel {
	get name() {
		return '1ppg1rpo'
	}
	getProposedEntities(concept: Concept, subconcepts: ConceptsByCid): ConceptProposedEntities {
		const entities = new ConceptProposedEntities()
		if(concept.title === null) {
			// concepts without titles cannot be persisted;
			// there is no natural key to match with the database
			return entities
		}
		switch(concept.valueDataType) {
			case ConceptValueDataType.Checks:
			case ConceptValueDataType.Radios:
			case ConceptValueDataType.Select:
				break
			default:
				throw new Error(ERR_INVALID_VALUEDATATYPE(concept, this))
		}
		const prompt = new ProposedPrompt(concept.cid, concept.title, PromptDataType.Boolean)
		entities.prompts.push(prompt)
		if(subconcepts !== null) {
			for(let subconceptCid in subconcepts) {
				let subconcept = subconcepts[subconceptCid]
				if(subconcept.title === null) {
					continue
				}
				let response = new ProposedResponse(subconcept.cid, subconcept.title)
				entities.responses.push(response)
				entities.responsesBySubconceptCid[subconceptCid] = response
				let intersection = new ProposedIntersection(prompt.stateKey, response.stateKey)
				entities.intersections.push(intersection)
			}
		}
		return entities
	}
	_getPrompt(proposedEntities: ConceptProposedEntities, returnedEntities: ReturnedEntityState) {
		if(proposedEntities.prompts.length !== 1) {
			throw new Error(`[model ${this.name} getPrompt] expected 1 proposed prompt`)
		}
		const promptKey = proposedEntities.prompts[0].stateKey
		if(!_.has(returnedEntities.prompt, promptKey)) {
			throw new Error(`[model ${this.name} getPrompt] expected prompt ${promptKey} not found ${SUGGEST_INTERSECTIONS}`)
		}
		return returnedEntities.prompt[promptKey]
	}
	_getResponses(proposedEntities: ConceptProposedEntities, returnedEntities: ReturnedEntityState): ReturnedResponse[] {
		let responses: ReturnedResponse[] = []
		for(let proposedResponse of proposedEntities.responses) {
			const responseKey = proposedResponse.stateKey
			if(!_.has(returnedEntities.response, responseKey)) {
				throw new Error(`[model ${this.name} getResponses] expected response ${responseKey} not found ${SUGGEST_INTERSECTIONS}`)
			}
			responses.push(returnedEntities.response[responseKey])
		}
		return responses
	}
	_getSubconceptResponse(proposedEntities: ConceptProposedEntities, returnedEntities: ReturnedEntityState, subconceptCid: string) {
		if(!_.has(proposedEntities.responsesBySubconceptCid, subconceptCid)) {
			throw new Error(`[model ${this.name} getSubconceptResponse] expected response ${subconceptCid} not found`)
		}
		const proposedResponse = proposedEntities.responsesBySubconceptCid[subconceptCid]
		const responseKey = proposedResponse.stateKey
		return returnedEntities.response[responseKey]
	}
	getReturnedEntities(proposedEntities: ConceptProposedEntities, returnedEntities: ReturnedEntityState): ReturnedEntities {
		const prompt = this._getPrompt(proposedEntities, returnedEntities)
		const responses = this._getResponses(proposedEntities, returnedEntities)
		return {
			prompts: [prompt],
			responses: responses,
		}
	}
})
